import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Shop, ShopMembership, MembershipRole, ShopContextType } from '../types';
import { useAuth } from './AuthContext';
import { shopService } from '../services/shopService';
import { auth } from '../config/firebase';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

const ShopContext = createContext<ShopContextType | undefined>(undefined);

interface ShopProviderProps {
  children: ReactNode;
}

export const ShopProvider: React.FC<ShopProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentShop, setCurrentShop] = useState<Shop | null>(null);
  const [userShops, setUserShops] = useState<Shop[]>([]);
  const [userMemberships, setUserMemberships] = useState<ShopMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformAdminStatus, setPlatformAdminStatus] = useState(false);

  // Load user shops and memberships
  useEffect(() => {
    if (user) {
      loadUserShopsAndMemberships();
      checkPlatformAdminStatus();
    } else {
      resetState();
    }
  }, [user]);

  const resetState = () => {
    setCurrentShop(null);
    setUserShops([]);
    setUserMemberships([]);
    setPlatformAdminStatus(false);
    setLoading(false);
  };

  const checkPlatformAdminStatus = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const token = await currentUser.getIdToken();
      const response = await fetch(`${SHOP_API_BASE_URL}/api/v1/admin/check`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPlatformAdminStatus(data.isPlatformAdmin === true);
      }
    } catch (error) {
      console.error('Failed to check platform admin status:', error);
      setPlatformAdminStatus(false);
    }
  };

  const loadUserShopsAndMemberships = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load user's shops and memberships in parallel
      const [shops, memberships] = await Promise.all([
        shopService.getUserShops(),
        shopService.getUserMemberships()
      ]);

      setUserShops(shops);
      setUserMemberships(memberships);

      if (shops.length > 0) {
        setCurrentShop(prev => {
          // If a shop is already selected, refresh its data but keep the selection
          if (prev) {
            const refreshed = shops.find(s => s.id === prev.id);
            if (refreshed) {
              console.log(`Refreshed current shop data: ${refreshed.name} (${refreshed.type})`);
              return refreshed;
            }
          }

          // Check URL hash for a shopId param (e.g., Stripe redirect: #shopAdmin/subscription?shopId=xxx)
          const hash = window.location.hash.replace('#', '');
          const queryString = hash.split('?')[1];
          const urlShopId = queryString ? new URLSearchParams(queryString).get('shopId') : null;
          if (urlShopId) {
            const urlShop = shops.find(s => s.id === urlShopId);
            if (urlShop) {
              console.log(`Selected shop from URL param: ${urlShop.name} (${urlShop.type})`);
              return urlShop;
            }
          }

          // No URL override - pick the default
          const defaultMembership = memberships.find(m => m.defaultShop && m.active);

          if (defaultMembership) {
            const defaultShop = shops.find(shop => shop.id === defaultMembership.shopId);
            if (defaultShop) {
              console.log(`Auto-selected default shop: ${defaultShop.name} (${defaultShop.type})`);
              return defaultShop;
            }
            console.log(`Default membership shop not found, using first available: ${shops[0].name}`);
            return shops[0];
          }

          // Fallback: no default membership, prefer user's own shop or first available
          const ownShop = shops.find(shop => shop.ownerId === user.uid);
          const fallbackShop = ownShop || shops[0];
          console.log(`No default membership, auto-selected: ${fallbackShop.name} (${fallbackShop.type})`);
          return fallbackShop;
        });
      } else {
        console.log('No shops available for user');
      }
    } catch (error) {
      console.error('Failed to load user shops and memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchShop = (shopId: string) => {
    const shop = userShops.find(s => s.id === shopId);
    if (shop) {
      setCurrentShop(shop);
    }
  };

  // Get current membership for the selected shop
  const currentMembership = currentShop
    ? userMemberships.find(m => m.shopId === currentShop.id && m.active) || null
    : null;

  // Get user's highest role in current shop
  const userRole: MembershipRole | null = currentMembership
    ? getHighestRole(currentMembership.roles)
    : null;

  // Role-based permissions
  const canViewPriceData = userRole ? canRoleViewPriceData(userRole) : false;
  const canManageSlides = userRole ? canRoleManageSlides(userRole) : false;
  const canManageShop = userRole ? canRoleManageShop(userRole) : false;
  const canManageMembers = userRole ? canRoleManageMembers(userRole) : false;
  const isInternalMember = userRole ? canRoleAccessAsInternal(userRole) : false;
  const isPlatformAdmin = platformAdminStatus;

  const contextValue: ShopContextType = {
    currentShop,
    userShops,
    userMemberships,
    currentMembership,
    switchShop,
    refreshShops: loadUserShopsAndMemberships,
    canViewPriceData,
    canManageSlides,
    canManageShop,
    canManageMembers,
    isInternalMember,
    isPlatformAdmin,
    userRole,
    loading
  };

  return (
    <ShopContext.Provider value={contextValue}>
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = (): ShopContextType => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

// Helper functions for role-based permissions

function getHighestRole(roles: MembershipRole[]): MembershipRole {
  const roleHierarchy: MembershipRole[] = [
    'PLATFORM_ADMIN',
    'OWNER',
    'ADMIN',
    'MEMBER',
    'VIEWER',
    'CLIENT',
    'VENDOR'
  ];

  for (const role of roleHierarchy) {
    if (roles.includes(role)) {
      return role;
    }
  }

  return 'VIEWER'; // Default fallback
}

function canRoleViewPriceData(role: MembershipRole): boolean {
  return ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].includes(role);
}

function canRoleManageSlides(role: MembershipRole): boolean {
  return ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER'].includes(role);
}

function canRoleManageShop(role: MembershipRole): boolean {
  return ['PLATFORM_ADMIN', 'OWNER', 'ADMIN'].includes(role);
}

function canRoleManageMembers(role: MembershipRole): boolean {
  return ['PLATFORM_ADMIN', 'OWNER', 'ADMIN'].includes(role);
}

// Internal (shop-staff) roles — the audience for Favorites + Named Collections.
// Excludes external CLIENT/VENDOR roles. Mirrors iOS ShopManager.isInternalMember.
function canRoleAccessAsInternal(role: MembershipRole): boolean {
  return ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].includes(role);
}